/**
 * PPT 幻灯片 — 与 .NET 后端约定（请后端按此实现；路径可加全局前缀如 /api）
 *
 * 1) GET  /ppt-modules/{moduleCode}/slides
 *    200: SlideDto[] 或 { slides: SlideDto[] } 或 { data: SlideDto[] }
 *    SlideDto: { id: string, imageUrl: string, sortOrder?: number }
 *
 * 2) POST /ppt-modules/{moduleCode}/slides
 *    Content-Type: multipart/form-data
 *    字段名: file（单文件）
 *    200: SlideDto（新建后的对象，可选）
 *
 * 3) PUT /ppt-modules/{moduleCode}/slides/{slideId}
 *    Content-Type: multipart/form-data
 *    字段名: file — 替换该张图片
 *
 * 4) DELETE /ppt-modules/{moduleCode}/slides/{slideId}
 *
 * 5) PUT /ppt-modules/{moduleCode}/slides/reorder
 *    Content-Type: application/json
 *    Body: { slideIds: string[] } — 完整顺序列表
 *
 * 全部走 @/utils/request 实例：Authorization 与拦截器统一；FormData 在 request 内去掉默认 JSON Content-Type。
 */
import instance from "@/utils/request";

const baseURL =
  process.env.NODE_ENV === "development"
    ? process.env.VUE_APP_DEV_DOMAIN
    : process.env.VUE_APP_DOMAIN;

/** 将后端返回的相对路径补全为可访问 URL（若已是 http(s) 则原样返回） */
export function resolveSlideImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== "string") {
    return "";
  }
  if (/^https?:\/\//i.test(imageUrl)) {
    return imageUrl;
  }
  const base = (baseURL || "").replace(/\/$/, "");
  const path = imageUrl.startsWith("/") ? imageUrl : "/" + imageUrl;
  return base + path;
}

function normalizeSlideList(res) {
  if (Array.isArray(res)) {
    return res;
  }
  if (res && Array.isArray(res.slides)) {
    return res.slides;
  }
  if (res && Array.isArray(res.data)) {
    return res.data;
  }
  return [];
}

/** @returns {Promise<Array<{ id: string, imageUrl: string, sortOrder?: number }>>} */
export function fetchSlidesList(moduleCode) {
  return instance
    .get(`/api/ppt-modules/${moduleCode}/slides`)
    .then(normalizeSlideList);
}

/** @returns {Promise<void>} */
export function deleteSlide(moduleCode, slideId) {
  return instance.delete(`/api/ppt-modules/${moduleCode}/slides/${slideId}`);
}

/** slideIds 为排好序的 id 列表（与展示顺序一致） */
export function reorderSlides(moduleCode, slideIds) {
  return instance.put(`/api/ppt-modules/${moduleCode}/slides/reorder`, {
    slideIds
  });
}

/** 新增一张（multipart） */
export function uploadSlide(moduleCode, file) {
  const fd = new FormData();
  fd.append("file", file);
  return instance.post(`/api/ppt-modules/${moduleCode}/slides`, fd);
}

/** 替换某张图片 */
export function replaceSlideImage(moduleCode, slideId, file) {
  const fd = new FormData();
  fd.append("file", file);
  return instance.put(`/api/ppt-modules/${moduleCode}/slides/${slideId}`, fd);
}
